import { 
  CheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { STAGE_PAGE_WIDTH_CLASS } from '../../constants/layout';
import { STAGE_DEFINITIONS } from '../../constants/stages';
import { ProjectStatus } from '../../types/project';

export interface ProjectStage {
  id: number;
  name: string;
  label: string;
  description: string;
  route: string;
  status: 'completed' | 'current' | 'available';
  isClickable: boolean;
}

interface StageNavigationBarProps {
  currentStage: number;
  projectId: number;
  projectStatus: ProjectStatus;
  onStageClick: (stageId: number, route: string) => void;
  className?: string;
  /** When true, shows the brownfield-conversion (SAHLD Conversion) stage in the navigation bar */
  isBrownfield?: boolean;
}

/**
 * StageNavigationBar - Horizontal progress bar with clickable stage indicators
 * Shows project workflow stages with completion status and navigation
 */
export const StageNavigationBar: React.FC<StageNavigationBarProps> = ({
  currentStage,
  projectId,
  projectStatus,
  onStageClick,
  className = '',
  isBrownfield = false,
}) => {

  /**
   * Determine stage status based on project status and current stage
   * Live document approach: all stages are accessible
   */
  const getStageStatus = (stageId: number): ProjectStage['status'] => {
    if (stageId < currentStage) {return 'completed';}
    if (stageId === currentStage) {return 'current';}
    return 'available'; // All stages are accessible in live document approach
  };

  /**
   * Map project stages with their properties using shared definitions.
   * Sorts by id and filters out the brownfield-conversion stage for greenfield projects.
   */
  const stages: ProjectStage[] = Object.values(STAGE_DEFINITIONS)
    .filter((def) => isBrownfield || def.name !== 'brownfield-conversion')
    .sort((a, b) => a.id - b.id)
    .map((stageDef) => ({
      id: stageDef.id,
      name: stageDef.name,
      label: stageDef.label,
      description: stageDef.description,
      route: `/project/${projectId}/${stageDef.name}`,
      status: getStageStatus(stageDef.id),
      isClickable: true, // All stages are always clickable in live document approach
    }));

  /**
   * Get icon for stage status
   */
  const getStageIcon = (stage: ProjectStage) => {
    switch (stage.status) {
      case 'completed':
        return <CheckIcon className="w-3 h-3 text-white" />;
      case 'current':
        return <ClockIcon className="w-3 h-3 text-white" />;
      case 'available':
        return <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />;
      default:
        return <div className="w-1.5 h-1.5 bg-white rounded-full" />;
    }
  };

  /**
   * Get CSS classes for stage indicator
   */
  const getStageClasses = (stage: ProjectStage) => {
    const baseClasses = 'flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200';
    
    switch (stage.status) {
      case 'completed':
        return `${baseClasses} bg-[#008557] text-white cursor-pointer hover:bg-[#00a368]`;
      case 'current':
        return `${baseClasses} bg-[rgb(255,198,0)] text-[rgb(41,41,41)] shadow-lg ring-2 ring-[rgb(255,198,0)] ring-opacity-50`;
      case 'available':
        return `${baseClasses} bg-gray-300 text-gray-500 cursor-pointer hover:bg-gray-400`;
      default:
        return `${baseClasses} bg-gray-300 text-gray-500`;
    }
  };

  /**
   * Get progress bar width percentage
   */
  const getProgressWidth = () => {
    const completedStages = stages.filter(stage => stage.status === 'completed').length;
    const currentProgress = stages.findIndex(stage => stage.status === 'current');
    const totalProgress = completedStages + (currentProgress >= 0 ? 0.5 : 0);
    return Math.min((totalProgress / (stages.length - 1)) * 100, 100);
  };

  /**
   * Handle stage click
   */
  const handleStageClick = (stage: ProjectStage) => {
    if (stage.isClickable) {
      onStageClick(stage.id, stage.route);
    }
  };

  return (
    <div className={`bg-white border-b border-gray-200 ${className}`}>
      <div className={`${STAGE_PAGE_WIDTH_CLASS} py-3`}>
        {/* Progress Overview */}
        <div className="mb-3">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-base font-semibold text-gray-900">Project Progress</h2>
            <span className="text-sm text-gray-500">
              {Math.round(getProgressWidth())}% Complete
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-[rgb(255,198,0)] h-1.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${getProgressWidth()}%` }}
            />
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <div className="flex justify-between items-center relative">
            {/* Connection Lines */}
            <div className="absolute top-3 left-4 right-4 h-0.5 bg-gray-200 -z-10" />
            <div 
              className="absolute top-3 left-4 h-0.5 bg-[rgb(255,198,0)] -z-10 transition-all duration-500"
              style={{
                width: `${Math.max(0, ((currentStage - 1) / Math.max(stages.length - 1, 1)) * 100)}%`,
              }}
            />

            {stages.map((stage, index) => (
              <div key={stage.id} className="flex flex-col items-center">
                {/* Stage Indicator */}
                <button
                  className={getStageClasses(stage)}
                  onClick={() => handleStageClick(stage)}
                  title={`${stage.label}: ${stage.description}`}
                >
                  {getStageIcon(stage)}
                </button>
                
                {/* Stage Label */}
                <div className="mt-1.5 text-center">
                  <div className={`text-xs font-medium ${
                    stage.status === 'current' ? 'text-black' : 
                      stage.status === 'completed' ? 'text-[#008557]' : 
                        'text-gray-500'
                  }`}>
                    {stage.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={getStageClasses(stages.find(s => s.status === 'current') || stages[0])}>
                {getStageIcon(stages.find(s => s.status === 'current') || stages[0])}
              </div>
              <div>
                <div className="font-medium text-gray-900">
                  {stages.find(s => s.status === 'current')?.label || stages[0].label}
                </div>
                <div className="text-sm text-gray-500">
                  Step {currentStage} of {stages.length}
                </div>
              </div>
            </div>
            
            {/* Mobile Stage Selector */}
            <select 
              value={currentStage}
              onChange={(e) => {
                const selectedStage = stages.find(s => s.id === Number.parseInt(e.target.value, 10));
                if (selectedStage?.isClickable) {
                  handleStageClick(selectedStage);
                }
              }}
              className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-[rgb(255,198,0)] focus:border-transparent"
            >
              {stages.map(stage => (
                <option 
                  key={stage.id} 
                  value={stage.id}
                  disabled={!stage.isClickable}
                >
                  {stage.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};
